// @flow
import React, { Component } from 'react';
import { connect } from 'react-redux';
import PropTypes from 'prop-types';
import dimensions from 'react-dimensions';
import { SimpleButton } from 'react-fast-ui';

import AdminRouter from './AdminRouter';
import FailedLoginScreen from './FailedLoginScreen';
import LoadingScreen from './layout/LoadingScreen';
import LogoutLink from './navigation/LogoutLink';
import NotificationSystemProvider from './NotificationSystemProvider';
import Overlay from './layout/Overlay';
import DAO from '../dao/DAO';
import Session from '../Session';
import { initializeLocalytics } from '../util/tracking/Localytics';
import { DEFAULT_ERROR_MESSAGE, EXPIRED_TOKEN_MESSAGE } from '../dao/helpers/getError';
import { SUPPORT_EMAIL } from '../constants/urls';
import { UserError } from '../models/User';
import { identifyUserForAppcues } from '../util/tracking/Appcues';

const VALIDATING = <LoadingScreen/>;

const EmailSupportLink = () => (
  <a
    href={`mailTo:${SUPPORT_EMAIL}`}
    className="mg-left-md color-primary hover-color-primary-darken no-underline">
    {SUPPORT_EMAIL}
  </a>
);

class AdminPortal extends Component {
  static childContextTypes = {
    contentWidth: PropTypes.number,
    windowWidth: PropTypes.number,
    dao: PropTypes.instanceOf(DAO),
    session: PropTypes.instanceOf(Session),
    ngUser: PropTypes.object,
  };

  static propTypes = {
    containerWidth: PropTypes.number,
  };

  state = {
    error: (null: ?UserError),
    failedLogin: false,
    session: new Session(),
    status: (null: ?string),
  };

  getChildContext() {
    const { containerWidth: windowWidth } = this.props;
    const { session } = this.state;
    const daoAndSession = session ? { dao: session.dao, session } : {};

    let ngUser = {};
    if (session.user) {
      ngUser = {
        ...session.user,
        user_id: session.user.userId,
        account_id: session.user.accountId,
        team_id: session.user.teamId,
        league_id: session.user.leagueId,
        features: session.user.features,
        isCoach: session.user.isCoach,
        isPlayer: session.user.isPlayer,
        isConsumer: session.user.isConsumer,
        roles: session.user.roles,
        availableProducts: session.user.availableProducts
      };
    }

    return { contentWidth: windowWidth, windowWidth, ngUser, ...daoAndSession };
  }

  componentDidMount() {
    const { session } = this.state;

    const startTimestamp = Date.now();

    session.initialize()
      .then((token) => this.handleValidLogin(token, startTimestamp), this.handleInvalidLogin)
      /* eslint-disable */
      .catch(error => {
        console.info(error.message);
        this.setState({ error });
      });

    /* eslint-enable */
  }

  isLoggedIn = () => Boolean(this.state.session && this.state.session.isLoggedIn && this.state.session.isLoggedIn());

  // handlers
  handleValidLogin = (token: Object, startTimestamp: number) => {
    const { session } = this.state;

    initializeLocalytics(window.config);
    identifyUserForAppcues(token.userDetails);
    const featureFlags = token.scopes.split(' ');
    if (token.access_token && featureFlags.includes('account_admin')) {
      this.setState({
        failedLogin: false,
        status: 'Logged in',
        session: session.onValidLogin(token, startTimestamp),
        featureFlags
      });
    } else {
      this.setState({
        failedLogin: true,
        status: 'User not authorized',
      });
    }
  };

  handleInvalidLogin = (err: Error) => {
    // if we have no helpful error message to display, just redirect to oauth login screen
    if (err.message === DEFAULT_ERROR_MESSAGE || err.message === EXPIRED_TOKEN_MESSAGE) {
      this.state.session.login();
    } else {
      this.setState({ failedLogin: true, status: err.message });
    }
  };

  render() {
    const { failedLogin, session, status, error } = this.state;
    const loggedOut = failedLogin ? <FailedLoginScreen errorMessage={status} onLogin={session.logout}/> : VALIDATING;
    if (error) {
      return (
        <div id="appContainer" className="position-relative">
          <Overlay className="justify-content-center">
            <div style={{ position: 'absolute', top: 10, left: 10 }}><LogoutLink className="display-block"/></div>
            <div className="text-align-center">
              <h1 className="xl-font mg-bottom-lg">
                An error has occurred. If this problem persists, please contact support at<EmailSupportLink/>
              </h1>
              <SimpleButton
                href='/#'
                onClick={() => {
                  this.state.session.logout();
                  this.state.session.login();
                }}
                style={{ display: 'inline-block', width: 200 }}>
                Log Out
              </SimpleButton>
            </div>
          </Overlay>
        </div>
      );
    }

    return (
      <div id="appContainer">
        {!this.isLoggedIn() ? loggedOut :
          <NotificationSystemProvider>
            <AdminRouter featureFlags={this.state.featureFlags}/>
          </NotificationSystemProvider>
        }
      </div>
    );
  }
}

export default connect()(dimensions()(AdminPortal));